/**
 * Ticket status — single source of truth for the whole frontend.
 *
 * Every screen that renders a ticket status badge or filters a ticket list by
 * status derives from the tables in this file. Before this existed, each screen
 * carried its own copy, and they disagreed: the field app's badge knew
 * `field_approved` means "Submitted" while its filter tab asked the API for a
 * status literally called `submitted`, which no row has ever had. Result: a tab
 * that rendered empty next to rows badged "Submitted".
 *
 * The DB vocabulary is fixed and owned by the API. Do not add values here that
 * the API cannot produce, and do not use this file to change a stored status —
 * it is display and filtering only.
 *
 * NOTE: the office ticket list (`src/screens/TicketsScreen.tsx`) still carries
 * its own `STATUS_CONFIG` / filter predicates. It is a protected file and its
 * tab vocabulary differs from the field app's on purpose (it splits Invoiced
 * and Rejected into their own tabs and labels the approval stages separately).
 * Migrating it onto the groups below is a deliberate follow-up, not a drive-by.
 */

/** Canonical status values the API stores. */
export const TICKET_STATUSES = [
  'draft',
  'field_approved',
  'office_approved',
  'customer_approved',
  'customer_rejected',
  'rejected',
  'invoiced',
  'archived',
] as const;

export type TicketStatus = (typeof TICKET_STATUSES)[number];

/**
 * Pre-migration values that may still arrive from an offline IndexedDB record
 * written by an older build. The API's own `normalizeLegacyStatus` maps
 * `active` → `draft`; the other two are historical aliases this app once wrote.
 */
const LEGACY_STATUS_ALIASES: Record<string, TicketStatus> = {
  active: 'draft',
  submitted: 'field_approved',
  approved: 'office_approved',
};

/** Coerce any stored/legacy status into the canonical vocabulary. */
export function normalizeTicketStatus(raw: string | null | undefined): TicketStatus {
  if (!raw) return 'draft';
  if ((TICKET_STATUSES as readonly string[]).includes(raw)) return raw as TicketStatus;
  return LEGACY_STATUS_ALIASES[raw] ?? 'draft';
}

/**
 * Workflow stage a status belongs to. Badges and tab filters both key off this,
 * which is what keeps them from drifting apart.
 */
export type TicketStatusGroup =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'invoiced'
  | 'archived';

export const TICKET_STATUS_GROUP: Record<TicketStatus, TicketStatusGroup> = {
  draft: 'draft',
  field_approved: 'submitted',
  office_approved: 'approved',
  customer_approved: 'approved',
  customer_rejected: 'rejected',
  rejected: 'rejected',
  invoiced: 'invoiced',
  archived: 'archived',
};

export function ticketStatusGroup(raw: string | null | undefined): TicketStatusGroup {
  return TICKET_STATUS_GROUP[normalizeTicketStatus(raw)];
}

/**
 * Office-side status labels — the strings the desk actually reads on screen.
 *
 * Exhaustive over `TicketStatus` on purpose: a fallback branch that echoes the
 * raw enum is how `field_approved` ends up in front of a user, and how a display
 * bug gets reported as a data bug.
 *
 * Deliberately NOT the same as `FIELD_TICKET_STATUS_LABELS` below. The field app
 * collapses the approval stages ("Submitted", "Approved") because a hand cares
 * that a ticket went up, not which desk holds it. That split is intentional —
 * see the header of this file.
 *
 * NOTE: `TicketDetail.tsx` and `TicketsScreen.tsx` still carry their own
 * `STATUS_CONFIG` maps, which pair these same labels with icons and colours.
 * Their canonical-status labels agree with this map. They are not folded in here
 * because both also key legacy aliases that disagree with each other
 * (`submitted` renders "Pending" in one and "Draft" in the other) and one is a
 * protected file. Unifying them is a deliberate follow-up, not a drive-by.
 */
export const OFFICE_TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  draft: 'Draft',
  field_approved: 'Field Approved',
  office_approved: 'Office Approved',
  customer_approved: 'Customer Approved',
  customer_rejected: 'Customer Rejected',
  rejected: 'Rejected',
  invoiced: 'Invoiced',
  archived: 'Archived',
};

export function officeTicketStatusLabel(raw: string | null | undefined): string {
  return OFFICE_TICKET_STATUS_LABELS[normalizeTicketStatus(raw)];
}

/**
 * ── Edit and delete locks ────────────────────────────────────────────────────
 *
 * Mirrors of the API's own lists in its `src/lib/ticket-status.ts`. As with
 * `TICKET_PDF_CUSTOMER_STATUSES` below, the API owns these rules and will refuse
 * us regardless of what this file says. These exist so a button that is going to
 * be refused can be disabled with a reason instead of 409-ing on click.
 *
 * Kept in one place because the old edit lock lived as a status literal copied
 * into each call site, and that is exactly why it drifted: API PR #311 had to
 * find and change every copy when the rule moved.
 */

/**
 * Statuses in which the API refuses an edit outright (409). Mirrors the API's
 * `FROZEN_STATUSES`. Once the customer has signed, or the ticket has been
 * billed, the numbers are a record of what was agreed.
 */
export const TICKET_FROZEN_STATUSES: readonly TicketStatus[] = ['customer_approved', 'invoiced'];

/**
 * Statuses in which the API refuses a DELETE. Mirrors the API's
 * `UNDELETABLE_STATUSES`.
 *
 * Deliberately WIDER than the frozen list, and not a copy of it — deleting is
 * not editing. Once the office has signed off, cert days have been applied and
 * the customer has usually been sent the document, so an office-approved ticket
 * may be corrected but not made to disappear.
 */
export const TICKET_UNDELETABLE_STATUSES: readonly TicketStatus[] = [
  'office_approved',
  'customer_approved',
  'invoiced',
];

/**
 * The status an edit reverts an office-approved ticket to. Mirrors the API's
 * `EDIT_REVERT_TARGET`. Back to the office, not all the way to the field — an
 * edit is a data change, not an un-submission.
 */
export const TICKET_EDIT_REVERT_TARGET: TicketStatus = 'field_approved';

/**
 * An absent status answers `false` for all three predicates below, breaking with
 * `normalizeTicketStatus`'s `'draft'` default for the same reason
 * `ticketPdfNeedsPreview` does: the two ways of being wrong are not symmetrical.
 * Guessing "locked" disables a button on a ticket the user is entitled to edit
 * and offers no way forward; guessing "open" costs at worst a 409 that the call
 * site already turns into a readable sentence. Prefer the recoverable mistake.
 */

/** True when the API will refuse to edit this ticket at all. */
export function ticketEditIsFrozen(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return TICKET_FROZEN_STATUSES.includes(normalizeTicketStatus(raw));
}

/** True when the API will refuse to delete this ticket. */
export function ticketDeleteIsBlocked(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return TICKET_UNDELETABLE_STATUSES.includes(normalizeTicketStatus(raw));
}

/**
 * True when saving an edit will REVERT the ticket rather than simply change it.
 * Only `office_approved` — every other editable status saves in place. This is
 * the whole reason the confirm dialog is conditional: warning on a
 * `field_approved` edit would describe consequences that do not happen.
 */
export function ticketEditReverts(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return normalizeTicketStatus(raw) === 'office_approved';
}

/**
 * Statuses `POST /tickets/:id/submit` will accept. Mirrors the API's own
 * `AND status IN ('draft', 'rejected')` in `tickets.routes.ts` — anything else
 * updates no rows and answers 409 "Must be draft or rejected."
 *
 * Both entries are a ticket nobody has stood behind yet: `draft` was never sent
 * up, `rejected` was sent back down by the office. Submitting is the act of
 * asserting the work happened, so it is only available where that assertion has
 * not already been made.
 */
export const TICKET_SUBMITTABLE_STATUSES: readonly TicketStatus[] = ['draft', 'rejected'];

/**
 * Statuses from which a ticket may be turned into an invoice. Mirrors the API's
 * `INVOICEABLE_STATUSES` in its `src/lib/ticket-status.ts`, which every API
 * generate / add-ticket site imports.
 *
 * `office_approved` is the entry that matters. Customer approval is a courtesy
 * step, not a billing control: the API and the 0230 trigger have always allowed
 * office_approved → invoiced, and only ONE frontend site — the Submit-as-Invoice
 * button on the ticket page — read `customer_approved` alone. That one outlier
 * is what convinced TNT that a customer who never clicks the link holds the
 * ticket unbillable. Global, not per-tenant (Jason, 2026-09-02).
 *
 * `invoiced` is excluded on purpose: the API refuses it (already billed), so
 * offering the button would be a 400 on click.
 */
export const TICKET_INVOICEABLE_STATUSES: readonly TicketStatus[] = ['office_approved', 'customer_approved'];

/** True when the API will accept this ticket on an invoice. */
export function ticketIsInvoiceable(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return TICKET_INVOICEABLE_STATUSES.includes(normalizeTicketStatus(raw));
}

/**
 * True when `POST /tickets/:id/submit` is legal for this ticket.
 *
 * An absent status answers `false`, and here that is NOT the "prefer the
 * recoverable mistake" asymmetry the edit-lock helpers above argue for — it
 * points the other way. Those guard a button that only reads; this one guards a
 * write. Guessing "submittable" fires a status transition on a ticket whose
 * status we do not know, and the failure is the one this helper exists to stop:
 * a 409 on a save that already succeeded, read by the user as a lost edit. The
 * editor also refuses to render a form at all when an edit target failed to
 * load, so `false` here costs a button on a screen that does not exist.
 *
 * Note this is judged on the status BEFORE the write. An edit to an
 * office_approved ticket reverts it to `field_approved`, so a submit chasing
 * that write is refused on the reverted status even if the original had been
 * legal — and `field_approved` is already the correct end state.
 */
export function ticketCanSubmit(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return TICKET_SUBMITTABLE_STATUSES.includes(normalizeTicketStatus(raw));
}

/**
 * Statuses for which `GET /tickets/:id/pdf` renders the real, customer-facing
 * document. Mirrors `PDF_ALLOWED_STATUSES` in the API's own
 * `src/lib/ticket-status.ts` (added by API PR #220) — the same list the
 * customer magic-link route has always enforced.
 *
 * Anything outside this list answers **409** unless the request asks for
 * `?preview=1`, which returns a watermarked, status-stamped PDF for any status.
 *
 * This is a *mirror*, not the source of truth: the API owns the list and will
 * reject us regardless of what this file says. It exists so the UI can tell the
 * user which document they are about to get before they click, and so the
 * request only carries `preview=1` when it actually needs it. If the API's list
 * changes, this one is stale until someone updates it — which is survivable
 * because every caller also handles the 409.
 */
export const TICKET_PDF_CUSTOMER_STATUSES: readonly TicketStatus[] = [
  'office_approved',
  'customer_approved',
  'customer_rejected',
  'invoiced',
];

/**
 * True when a ticket's PDF must be requested with `preview=1` to come back at
 * all — i.e. the ticket is not yet (or never became) customer-facing.
 *
 * An absent status answers `false`, deliberately breaking with
 * `normalizeTicketStatus`, which defaults to `'draft'`. A payload that omits
 * `status` tells us nothing, and the two ways of being wrong are not
 * symmetrical: guessing "preview" would hand a watermarked page to someone
 * entitled to the real one and say nothing about it, while guessing "real"
 * costs at worst a 409 that every call site turns into a readable sentence.
 * Prefer the recoverable mistake.
 */
export function ticketPdfNeedsPreview(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return !TICKET_PDF_CUSTOMER_STATUSES.includes(normalizeTicketStatus(raw));
}

/**
 * Labels used by the field app (My Tickets). Deliberately coarser than the
 * office list's: a hand cares that a ticket went up, not which desk holds it.
 */
export const FIELD_TICKET_STATUS_LABELS: Record<TicketStatusGroup, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  invoiced: 'Invoiced',
  archived: 'Archived',
};

export function fieldTicketStatusLabel(raw: string | null | undefined): string {
  return FIELD_TICKET_STATUS_LABELS[ticketStatusGroup(raw)];
}

export interface FieldTicketTab {
  key: string;
  label: string;
  /** Groups this tab shows. `null` means every ticket, whatever its status. */
  groups: TicketStatusGroup[] | null;
}

/**
 * My Tickets filter tabs, in display order.
 *
 * Open is everything a hand can still act on — a rejected ticket is work in
 * progress, an invoiced one is not. Approved folds invoiced in because from the
 * field's side an invoiced ticket is simply an approved one that moved on.
 * All sits last, as the terminal escape hatch.
 */
export const FIELD_TICKET_TABS: readonly FieldTicketTab[] = [
  { key: 'open', label: 'Open', groups: ['draft', 'submitted', 'rejected'] },
  { key: 'draft', label: 'Draft', groups: ['draft'] },
  { key: 'submitted', label: 'Submitted', groups: ['submitted'] },
  { key: 'approved', label: 'Approved', groups: ['approved', 'invoiced'] },
  { key: 'all', label: 'All', groups: null },
];

export function matchesFieldTicketTab(tabKey: string, status: string | null | undefined): boolean {
  const tab = FIELD_TICKET_TABS.find(t => t.key === tabKey);
  if (!tab || tab.groups === null) return true;
  return tab.groups.includes(ticketStatusGroup(status));
}

/**
 * Predicate for a tab, or `undefined` for tabs that show everything — matching
 * the shape `useOfflineQuery`'s `filterFn` option expects.
 */
export function fieldTicketTabFilter(
  tabKey: string
): ((t: { status?: string | null }) => boolean) | undefined {
  const tab = FIELD_TICKET_TABS.find(t => t.key === tabKey);
  if (!tab || tab.groups === null) return undefined;
  return (t: { status?: string | null }) => matchesFieldTicketTab(tabKey, t?.status);
}
